// apps/dashboard/src/lib/post-auth-redirect.ts
import { getRedirectUrl } from '@/lib/redirect-url';
import { safeLocal } from '@/lib/safe-storage';

// localStorage, not sessionStorage: the verification email opens in a NEW tab, which would
// not see a sessionStorage value written by the register tab.
const KEY = 'postAuthRedirect';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Auth and onboarding screens are never a sensible "where I was headed" target, and
// sending someone back into one after verifying would loop them.
const BLOCKED_SEGMENTS = ['auth', 'login', 'register', 'onboarding', 'verify-email'];

/**
 * Returns `value` when it is a same-origin relative path that is safe to navigate to after
 * auth, otherwise null. Builds on getRedirectUrl (rejects `//`, backslashes, tabs/newlines,
 * foreign origins) and additionally refuses absolute URLs and the auth/onboarding pages.
 */
export function sanitizePostAuthPath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  if (getRedirectUrl(value) !== value) return null;
  const firstSegment = value.slice(1).split(/[/?#]/)[0];
  return BLOCKED_SEGMENTS.includes(firstSegment) ? null : value;
}

/**
 * Remembers where the person was headed. Invalid values are ignored and keep any earlier one.
 * Called from the register page only: an email registration is the one flow that ends in a
 * verification link. Writing it on login or onboarding too would leave a value behind for every
 * already-verified user, for whoever verifies next in this browser to inherit.
 */
export function rememberPostAuthPath(value: unknown): void {
  const path = sanitizePostAuthPath(value);
  // '/' is the default anyway; storing it would only overwrite a real destination.
  if (!path || path === '/') return;
  safeLocal.setJSON(KEY, { path, at: Date.now() });
}

/** Returns the remembered destination and clears it; null when none, stale, tampered or storage is blocked. */
export function consumePostAuthPath(): string | null {
  const stored = safeLocal.getJSON<{ path?: unknown; at?: unknown } | null>(KEY, null);
  safeLocal.remove(KEY);
  if (!stored || typeof stored.at !== 'number' || Date.now() - stored.at > MAX_AGE_MS) return null;
  return sanitizePostAuthPath(stored.path);
}
